import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import ReviewInvestmentImg from "../../assets/Investments/reviewinvestment.png";
import StartInvestmentImg from "../../assets/Investments/startinvestment.png";
import InvestmentPortfolioImg from "../../assets/Investments/investmentportfolio.png";
// import GlobalMarketImg from "../../assets/Investments/globalmarket.png";
import TrustedPartnersImg from "../../assets/Investments/trustedpartners.png";
import TaxEfficientImg from "../../assets/Investments/taxefficient.png";
import { Helmet } from "react-helmet-async";
import "./Investments.css";

const Investments: React.FC = () => {
  const investmentInfo = {
    "Review My Investments":
      "Review My Investments lets you assess and adjust your portfolio to ensure it aligns with your financial goals and growth aspirations.",
    "Start a New Investment":
      "Begin building your financial future with our holistic approach - we'll align new investments with your pension strategy and protection needs.",
    "Exclusive Investment Portfolios":
      "Gain access to exclusive investment opportunities tailored to your risk profile and financial goals. Our advisors help you maximise returns while minimising risks.",
    // "Global Market Investments":
    //   "Diversify your portfolio with international investment products that fit within your overall financial plan, including pension and protection strategies.",
    "Trusted Investment Partners":
      "We partner with top financial institutions, including Aviva, Zurich, and Royal London, ensuring you have access to the most secure and high-performing investment products.",
    "Tax-Efficient Growth Strategies":
      "Optimise your investments for maximum tax efficiency. Take advantage of tax-saving opportunities while growing your wealth in a sustainable manner.",
  };
  const navigate = useNavigate();

  const handleRedirect = () => {
    navigate("/contact/");
    setTimeout(() => {
      const element = document.getElementById("get-in-touch");
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
      },
    },
  };

  const fadeInVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.8 } },
  };

  const [heroRef, heroInView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  return (
    <div className="investments">
      <Helmet>
        <title>
          Investments - Apollo Financial Services | Apollo Financial Services
          Ireland
        </title>
        <meta
          name="description"
          content="Expert investment management services in Ireland. Portfolio reviews, tax-efficient strategies, and exclusive investment opportunities from trusted advisors."
        />
        <meta
          name="keywords"
          content="Apollo Financial Duleek, Apollo Financial Dublin, Apollo Financial Meath, Apollo Financial Ireland, Apollo Financial pension services, Apollo Financial investment advice, Apollo Financial retirement planning, Apollo Financial tax planning, Apollo Financial estate planning, Apollo Financial mortgage advice, Apollo Financial financial consultants, Apollo Financial wealth management, Apollo Financial financial planning, Apollo Financial services Ireland, Apollo Financial client reviews, Apollo Financial advisor team, Apollo Financial contact details, Apollo Financial office hours, Apollo Financial appointment booking, Apollo Financial online consultation, Apollo Financial blog, Apollo Financial news, Apollo Financial events, Apollo Financial webinars, Apollo Financial seminars, Apollo Financial workshops, Apollo Financial resources, Apollo Financial FAQs, Apollo Financial testimonials, Apollo Financial case studies, financial planning for small business owners, financial planning for self-employed professionals, financial advice for contractors in Ireland, financial planning for corporate executives, wealth management for company directors, tax strategies for sole traders Ireland, financial advice for nurses Ireland, pension planning for teachers in Ireland, financial planner for civil servants Ireland, retirement planning for engineers, investment planning for freelancers, pension options for gig economy workers, life insurance for business owners, income protection for self-employed, tax planning for IT consultants, corporate pension advice Ireland, investment options for high earners, financial planner for landlords, tax strategy for property investors, pension consolidation advice Ireland"
        />
        <link
          rel="canonical"
          href="https://www.apollofinancial.ie/investments"
        />
      </Helmet>

      {/* Add Structured Data */}
      <script type="application/ld+json">
        {`
    {
      "@context": "https://schema.org",
      "@type": "FinancialService",
      "name": "Apollo Financial Services Investment Solutions",
      "description": "Professional investment management services in Ireland",
      "url": "https://www.apollofinancial.ie/investments/",
      "serviceType": "Investment Management",
      "image": "https://www.apollofinancial.ie/assets/logo2.png",
      "telephone": "01 689 5066",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Thomastown House, Thomastown",
        "addressLocality": "Duleek",
        "addressRegion": "Co. Meath",
        "postalCode": "A92 X362",
        "addressCountry": "IE"
      },
      "offers": {
        "@type": "Offer",
        "name": "Investments",
        "description": "Comprehensive investment services including portfolio reviews, new investment strategies, and tax-efficient planning",
        "url": "https://www.apollofinancial.ie/investments/"
      },
      "provider": {
        "@type": "Organization",
        "name": "Apollo Financial Services",
        "sameAs": [
          "https://www.facebook.com/ApolloFinancialServicesLtd",
          "https://www.linkedin.com/company/apollo-financial-services-limited/"
        ]
      }
    }
  `}
      </script>

      {/* Hero Section */}
      <motion.section
        ref={heroRef}
        className="investments-hero"
        initial="hidden"
        animate={heroInView ? "visible" : "hidden"}
        variants={fadeInVariants}
      >
        <div className="hero-overlay"></div>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={heroInView ? "visible" : "hidden"}
        >
          <motion.h1 variants={itemVariants}>
            Smart Investment Solutions
          </motion.h1>
          <motion.p variants={itemVariants}>
            Tailored strategies to grow and protect your wealth.
          </motion.p>
        </motion.div>
      </motion.section>

      {/* Call to Action Section */}
      <div className="investments-actions">
        {[
          {
            title: "Review My Investments",
            description: investmentInfo["Review My Investments"],
            img: ReviewInvestmentImg,
          },
          {
            title: "Start a New Investment",
            description: investmentInfo["Start a New Investment"],
            img: StartInvestmentImg,
          },
          {
            title: "Exclusive Investment Portfolios",
            description: investmentInfo["Exclusive Investment Portfolios"],
            img: InvestmentPortfolioImg,
          },
          // {
          //   title: "Global Market Investments",
          //   description: investmentInfo["Global Market Investments"],
          //   img: GlobalMarketImg,
          // },
          {
            title: "Trusted Investment Partners",
            description: investmentInfo["Trusted Investment Partners"],
            img: TrustedPartnersImg,
          },
          {
            title: "Tax-Efficient Growth Strategies",
            description: investmentInfo["Tax-Efficient Growth Strategies"],
            img: TaxEfficientImg,
          },
        ].map((item, index) => {
          return (
            <div
              className={`investment-section ${
                index % 2 === 0 ? "text-left" : "text-right"
              }`}
              key={index}
            >
              <div className="investment-content">
                <h2>{item.title}</h2>
                <p>{item.description}</p>
                <button className="investment-btn" onClick={handleRedirect}>
                  Find Out More →
                </button>
              </div>
              <div className="investment-image">
                <img src={item.img} alt={item.title} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Why Investments Matter Section */}
      <section className="investments-content-section">
        <div className="investments-intro">
          <h2>Why Invest with Us?</h2>
          <p>
            Our investment strategies are designed to help you grow your wealth
            while managing risk. Whether you're looking for high-growth
            opportunities or stable long-term investments, we have the right
            solutions for you.
          </p>
        </div>

        <div className="investment-services">
          <div className="investment-service-item">
            <img
              src={InvestmentPortfolioImg}
              alt="Wealth Growth"
              className="investment-service-image"
            />
            <div className="investment-service-content">
              <h3>Exclusive Investment Portfolios</h3>
              <p>
                ✔ Gain access to premium investment opportunities and tailor
                your portfolio for optimal growth.
              </p>
            </div>
          </div>

          {/* <motion.div
            className="investment-service-item investment-reverse"
            initial="hidden"
            animate={servicesInView ? "visible" : "hidden"}
            variants={{
              hidden: { opacity: 0, y: 30 },
              visible: {
                opacity: 1,
                y: 0,
                transition: { duration: 0.6, delay: 0.4 },
              },
            }}
          >
            <motion.img
              src={GlobalMarketImg}
              alt="Global Markets"
              className="investment-service-image"
              whileHover={{ scale: 1.03 }}
              transition={{ duration: 0.3 }}
            />
            <div className="investment-service-content">
              <h3>Global Market Access</h3>
              <p>
                ✔ Expand your investments beyond local markets and diversify
                your risk effectively.
              </p>
            </div>
          </motion.div> */}

          <div className="investment-service-item">
            <img
              src={TrustedPartnersImg}
              alt="Trusted Providers"
              className="investment-service-image"
            />
            <div className="investment-service-content">
              <h3>Trusted Investment Partners</h3>
              <p>
                ✔ Invest with confidence in financial products from top-tier
                institutions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="investments-why">
        <h2>Why Choose Our Investment Solutions?</h2>
        <div className="investments-features">
          <div className="investments-feature-item">
            <h3>Expert Investment Strategies</h3>
            <p>
              Our team helps you identify the best opportunities tailored to
              your financial goals.
            </p>
          </div>
          <div className="investments-feature-item">
            <h3>Balanced Risk & Reward</h3>
            <p>
              We create portfolios that optimise growth while managing risk
              effectively.
            </p>
          </div>
          <div className="investments-feature-item">
            <h3>Tax-Efficient Growth</h3>
            <p>
              Optimise your investment strategy with tax-efficient solutions.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Investments;
